import React, { useState, useEffect, useCallback } from "react";
import {
  Database,
  BarChart3,
  AlertCircle,
  CheckCircle,
  Trash2,
  X,
  Play,
  FileText,
  Megaphone,
  Package,
  FolderLock,
  Layers,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useChat } from "../contexts/ChatContext";
import { productsApi } from "../services/productsApi";
import { fileApi } from "../services/api";
import { useModal } from "../hooks/useModal";

const VectorStoreStats = () => {
  const { translate } = useLanguage();
  const { isEffectivelyAdmin } = useAuth();
  const { chats } = useChat();
  const activeChatsCount = chats ? chats.filter(chat => chat.is_active).length : 0;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmationText, setConfirmationText] = useState("");
  const [reindexStatus, setReindexStatus] = useState(null);
  const [reindexLoading, setReindexLoading] = useState(false);

  // Fetch re-indexation status
  const fetchReindexStatus = useCallback(async () => {
    try {
      const response = await productsApi.getIngestionStatus();
      // Only show reindex status (not product ingestion)
      if (response && response.operation_type === "reindex") {
        setReindexStatus(response);
      } else {
        setReindexStatus(null);
      }
    } catch (err) {
      console.warn("Failed to fetch reindex status:", err);
    }
  }, []);

  // Poll reindex status when running
  useEffect(() => {
    let interval;
    if (reindexStatus && reindexStatus.status === "running") {
      interval = setInterval(() => {
        fetchReindexStatus();
      }, 2000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [reindexStatus, fetchReindexStatus]);

  const handleReindexAll = async () => {
    try {
      setReindexLoading(true);
      setError(null);
      await fileApi.reindexAll();
      await fetchReindexStatus();
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        translate("failedToStartReindexation") ||
        "Failed to start re-indexation";
      setError(errorMessage);
    } finally {
      setReindexLoading(false);
    }
  };

  // Fetch vector store statistics
  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await productsApi.getVectorStoreStats();
      setStats(response);
    } catch (err) {
      let errorMessage =
        translate("failedToFetchVectorStoreStats") ||
        "Failed to fetch vector store statistics";

      if (err.response?.status === 401) {
        errorMessage =
          translate("unauthorizedAccess") ||
          "Unauthorized access. Please log in again.";
      } else if (err.response?.status === 403) {
        errorMessage =
          translate("insufficientPermissionsStats") ||
          "Insufficient permissions to view vector store statistics.";
      } else if (err.response?.status === 500) {
        errorMessage =
          translate("serverErrorStats") ||
          "Server error occurred while fetching statistics. Please try again.";
      } else if (err.code === "NETWORK_ERROR" || !err.response) {
        errorMessage =
          translate("networkErrorStats") ||
          "Network error. Please check your connection and try again.";
      } else if (err.message) {
        errorMessage += `: ${err.message}`;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [translate]);

  // Fetch stats on component mount
  useEffect(() => {
    fetchStats();
    fetchReindexStatus();
  }, [fetchStats, fetchReindexStatus]);

  // Clear collection
  const handleClearCollection = (collectionName) => {
    const collection = stats?.collections?.find(
      (c) => c.name === collectionName
    );
    const vectorCount = collection?.vectors_count || 0;

    setConfirmAction({
      type: "collection",
      collectionName,
      vectorCount,
      title:
        translate("clearCollectionConfirmation") ||
        "Are you sure you want to clear the collection?",
      description: `${collectionName}`,
      details: `Vectors: ${vectorCount.toLocaleString()}\nFiles: Will be deleted from file management`,
      warning:
        translate("actionCannotBeUndone") || "This action cannot be undone!",
      confirmationText: collectionName,
    });
    setConfirmationText("");
    setShowConfirmModal(true);
  };

  const executeClearCollection = async () => {
    try {
      setLoading(true);

      // Clear vectors
      await productsApi.clearVectorStoreCollection(
        confirmAction.collectionName
      );

      // Clear files (only for collections that have physical files)
      if (confirmAction.collectionName !== "products") {
        await productsApi.clearCollectionFiles(confirmAction.collectionName);
      }

      await fetchStats(); // Refresh stats
      setShowConfirmModal(false);
      setConfirmAction(null);

      // Show success message
      toast.success(
        translate("collectionClearedSuccessfully") ||
          `Collection "${confirmAction.collectionName}" cleared successfully!`
      );
    } catch (err) {
      let errorMessage =
        translate("failedToClearCollection") || "Failed to clear collection";

      if (err.response?.status === 403) {
        errorMessage =
          translate("insufficientPermissions") ||
          "Insufficient permissions. Only administrators can clear collections.";
      } else if (err.response?.status === 404) {
        errorMessage =
          translate("collectionNotFound") ||
          `Collection "${confirmAction.collectionName}" not found.`;
      } else if (err.response?.status === 500) {
        errorMessage =
          translate("serverErrorClearCollection") ||
          "Server error occurred while clearing collection. Please try again.";
      } else if (err.message) {
        errorMessage += `: ${err.message}`;
      }

      setError(errorMessage);
      setShowConfirmModal(false);
      setConfirmAction(null);
    } finally {
      setLoading(false);
    }
  };


  // Clear all collections
  const handleClearAll = () => {
    const collectionsToClear = stats?.collections || [];

    const totalVectors =
      collectionsToClear.reduce(
        (sum, collection) => sum + collection.vectors_count,
        0
      ) || 0;

    setConfirmAction({
      type: "all",
      totalVectors,
      collectionsCount: collectionsToClear.length || 0,
      title:
        translate("clearAllCollectionsConfirmation") ||
        "Are you sure you want to clear ALL collections?",
      description: translate("allCollections") || "All Collections",
      details: `Collections: ${
        collectionsToClear.length || 0
      }\nTotal Vectors: ${totalVectors.toLocaleString()}\nFiles: Will be deleted from all collections`,
      warning:
        translate("actionCannotBeUndone") || "This action cannot be undone!",
      confirmationText: "CLEAR ALL",
    });
    setConfirmationText("");
    setShowConfirmModal(true);
  };

  const executeClearAll = async () => {
    try {
      setLoading(true);

      // Clear all vectors
      await productsApi.clearAllVectorStoreCollections();

      // Clear all files
      await productsApi.clearAllCollectionFiles();

      await fetchStats(); // Refresh stats
      setShowConfirmModal(false);
      setConfirmAction(null);

      // Show success message
      toast.success(
        translate("allCollectionsClearedSuccessfully") ||
          "All collections cleared successfully!"
      );
    } catch (err) {
      let errorMessage =
        translate("failedToClearAllCollections") ||
        "Failed to clear all collections";

      if (err.response?.status === 403) {
        errorMessage =
          translate("insufficientPermissionsClearAll") ||
          "Insufficient permissions. Only administrators can clear all collections.";
      } else if (err.response?.status === 500) {
        errorMessage =
          translate("serverErrorClearAllCollections") ||
          "Server error occurred while clearing all collections. Please try again.";
      } else if (err.message) {
        errorMessage += `: ${err.message}`;
      }

      setError(errorMessage);
      setShowConfirmModal(false);
      setConfirmAction(null);
    } finally {
      setLoading(false);
    }
  };

  const getCollectionIcon = (collectionName) => {
    const icons = {
      general_information: FileText,
      campaigns: Megaphone,
      products: Package,
      internal_procedures: FolderLock,
    };
    return icons[collectionName] || Layers;
  };

  const getCollectionDisplayName = (collectionName) => {
    const names = {
      general_information:
        translate("generalInformation") || "General Information",
      campaigns: translate("campaigns") || "Campaigns",
      products: translate("products") || "Products",
      internal_procedures: translate("internalProcedures") || "Internal Procedures",
    };
    return names[collectionName] || collectionName;
  };

  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmationText("");
  };

  const confirmModalRef = useModal(showConfirmModal, handleCloseConfirmModal);

  // Show all collections returned by the API
  const filteredCollections = stats?.collections || [];

  const totalVectors =
    filteredCollections.reduce(
      (sum, collection) => sum + collection.vectors_count,
      0
    ) || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: "var(--color-text-primary)" }}>
            {translate("vectorStoreStatistics") || "Vector Store Statistics"}
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
            {translate("vectorStoreStatisticsDescription") ||
              "Monitor vector store collections and system health"}
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 rounded-xl flex items-center justify-between" style={{ backgroundColor: "var(--color-error-muted, #fef2f2)", border: "1px solid var(--color-error-border, #fecaca)" }}>
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" style={{ color: "var(--color-error)" }} />
            <span style={{ color: "var(--color-error)" }}>{error}</span>
          </div>
          <button
            onClick={() => setError(null)}
            style={{ color: "var(--color-error)" }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Loading State */}
      {!stats && !error && (
        <div className="space-y-6 animate-pulse">
          {/* Summary Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="card p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-xl bg-[var(--color-skeleton)]"></div>
                  </div>
                  <div className="ml-4 flex-1">
                    <div className="h-4 rounded-xl w-24 mb-2 bg-[var(--color-skeleton)]"></div>
                    <div className="h-8 rounded-xl w-16 bg-[var(--color-skeleton-light)]"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Collections Details Skeleton */}
          <div className="card">
            <div className="px-6 py-4 border-b border-[var(--color-border)]" style={{ backgroundColor: "var(--color-surface-raised)" }}>
              <div className="flex items-center justify-between">
                <div className="h-6 rounded-xl w-48 bg-[var(--color-skeleton)]"></div>
                <div className="h-8 rounded-xl w-24 bg-[var(--color-skeleton-light)]"></div>
              </div>
            </div>

            <div className="divide-y divide-[var(--color-border)]">
              {[1, 2, 3].map((i) => (
                <div key={i} className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="h-8 w-8 rounded-xl bg-[var(--color-skeleton)]"></div>
                      <div>
                        <div className="h-5 rounded-xl w-32 mb-2 bg-[var(--color-skeleton)]"></div>
                        <div className="h-4 rounded-xl w-24 bg-[var(--color-skeleton-light)]"></div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-6">
                      {[1, 2, 3].map((j) => (
                        <div key={j} className="text-center">
                          <div className="h-4 rounded-xl w-16 mb-2 bg-[var(--color-skeleton)]"></div>
                          <div className="h-6 rounded-xl w-12 bg-[var(--color-skeleton-light)]"></div>
                        </div>
                      ))}
                      <div className="h-8 rounded-xl w-20 bg-[var(--color-skeleton-light)]"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {stats && filteredCollections.length === 0 && (
        <div className="text-center py-12">
          <Database className="h-12 w-12 mx-auto mb-4" style={{ color: "var(--color-text-muted)" }} />
          <h3 className="text-base font-semibold mb-2" style={{ color: "var(--color-text-primary)" }}>
            {translate("noCollections") || "No Collections Found"}
          </h3>
          <p className="mb-4" style={{ color: "var(--color-text-secondary)" }}>
            {translate("noCollectionsDescription") ||
              "No vector store collections are available."}
          </p>
        </div>
      )}

      {/* Statistics Content */}
      {stats && filteredCollections.length > 0 && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="card p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Database className="h-8 w-8" style={{ color: "var(--color-mint)" }} />
                </div>
                <div className="ml-4">
                  <p className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--color-text-muted)" }}>
                    {translate("totalCollections") || "Total Collections"}
                  </p>
                  <p className="text-2xl font-semibold" style={{ color: "var(--color-text-primary)" }}>
                    {filteredCollections.length || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BarChart3 className="h-8 w-8" style={{ color: "var(--color-mint)" }} />
                </div>
                <div className="ml-4">
                  <p className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--color-text-muted)" }}>
                    {translate("totalVectors") || "Total Vectors"}
                  </p>
                  <p className="text-2xl font-semibold" style={{ color: "var(--color-text-primary)" }}>
                    {totalVectors.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-8 w-8" style={{ color: "var(--color-success)" }} />
                </div>
                <div className="ml-4">
                  <p className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--color-text-muted)" }}>
                    {translate("systemHealth") || "System Health"}
                  </p>
                  <p className="text-2xl font-semibold" style={{ color: "var(--color-success)" }}>
                    {translate("healthy") || "Healthy"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Re-indexation Status - Admin Only */}
          {isEffectivelyAdmin() && (
            <div className="card rounded-xl border border-[var(--color-border)] p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-mint-muted)" }}>
                    <Play className="h-4 w-4" style={{ color: "var(--color-mint)" }} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
                      {translate("reindexationStatus") || "Re-indexation"}
                    </h3>
                    <p className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
                      {translate("reindexationDescription") ||
                        "Re-process all files with the current embedding model"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleReindexAll}
                  disabled={reindexLoading || (reindexStatus && reindexStatus.status === "running")}
                  className="btn-primary disabled:opacity-50 flex-shrink-0"
                >
                  {reindexLoading ? (
                    <div style={{ width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: 8 }} />
                  ) : (
                    <Play className="h-4 w-4 mr-2" />
                  )}
                  {translate("runReindexation") || "Run Re-indexation"}
                </button>
              </div>

              {reindexStatus && reindexStatus.status !== "idle" ? (
                <div className="space-y-3 mt-4">
                  <div
                    className="p-4 rounded-xl"
                    style={{
                      backgroundColor: reindexStatus.status === "completed"
                        ? "rgba(34,197,94,0.06)"
                        : reindexStatus.status === "running"
                        ? "rgba(59,130,246,0.06)"
                        : reindexStatus.status === "failed"
                        ? "rgba(239,68,68,0.06)"
                        : "var(--color-surface-raised)",
                      borderLeft: `4px solid ${
                        reindexStatus.status === "completed" ? "var(--color-success)"
                        : reindexStatus.status === "running" ? "var(--color-info)"
                        : reindexStatus.status === "failed" ? "var(--color-error)"
                        : "var(--color-border)"
                      }`,
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {reindexStatus.status === "completed" && (
                          <CheckCircle className="h-5 w-5 mr-2" style={{ color: "var(--color-success)" }} />
                        )}
                        {reindexStatus.status === "running" && (
                          <div style={{ width: '20px', height: '20px', border: '2px solid var(--color-info)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: 8 }} />
                        )}
                        {reindexStatus.status === "failed" && (
                          <AlertCircle className="h-5 w-5 mr-2" style={{ color: "var(--color-error)" }} />
                        )}
                        <span className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                          {translate("status") || "Status"}
                        </span>
                      </div>
                      <span
                        className={`px-3 py-1 text-xs font-medium rounded-full ${
                          reindexStatus.status === "completed"
                            ? "badge-active"
                            : reindexStatus.status === "running"
                            ? "badge-mint"
                            : reindexStatus.status === "failed"
                            ? "bg-red-50 text-red-600 border border-red-200"
                            : "badge-inactive"
                        }`}
                      >
                        {reindexStatus.status === "completed"
                          ? translate("completed") || "Completed"
                          : reindexStatus.status === "running"
                          ? translate("running") || "Running"
                          : reindexStatus.status === "failed"
                          ? translate("failed") || "Failed"
                          : reindexStatus.status}
                      </span>
                    </div>

                    {reindexStatus.message && (
                      <p className="mt-2 text-sm" style={{ color: "var(--color-text-secondary)" }}>
                        {reindexStatus.message}
                      </p>
                    )}
                  </div>

                  {(reindexStatus.started_at || reindexStatus.completed_at) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {reindexStatus.started_at && (
                        <div className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: "var(--color-surface-raised)" }}>
                          <span className="text-xs font-medium" style={{ color: "var(--color-text-muted)" }}>
                            {translate("startedAt") || "Started"}
                          </span>
                          <span className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
                            {typeof reindexStatus.started_at === "number"
                              ? new Date(reindexStatus.started_at * 1000).toLocaleString()
                              : reindexStatus.started_at}
                          </span>
                        </div>
                      )}
                      {reindexStatus.completed_at && (
                        <div className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: "var(--color-surface-raised)" }}>
                          <span className="text-xs font-medium" style={{ color: "var(--color-text-muted)" }}>
                            {translate("completedAt") || "Completed"}
                          </span>
                          <span className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
                            {typeof reindexStatus.completed_at === "number"
                              ? new Date(reindexStatus.completed_at * 1000).toLocaleString()
                              : reindexStatus.completed_at}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {reindexStatus.error && (
                    <div className="p-3 rounded-xl" style={{ backgroundColor: "var(--color-error-muted)", border: "1px solid var(--color-error-border)" }}>
                      <div className="flex items-start">
                        <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" style={{ color: "var(--color-error)" }} />
                        <span className="text-sm" style={{ color: "var(--color-error)" }}>
                          {reindexStatus.error}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}

          {/* Collections Details */}
          <div className="card">
            <div className="px-6 py-4 border-b border-[var(--color-border)]" style={{ backgroundColor: "var(--color-surface-raised)" }}>
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>
                  {translate("collectionsDetails") || "Collections Details"}
                </h3>
                {isEffectivelyAdmin() && (
                  <button
                    onClick={handleClearAll}
                    disabled={loading}
                    className="btn-destructive flex items-center space-x-2 disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>{translate("clearAll") || "Clear All"}</span>
                  </button>
                )}
              </div>
            </div>

            <div className="divide-y divide-[var(--color-border)]">
              {filteredCollections.map((collection, index) => {
                  const IconComponent = getCollectionIcon(collection.name);
                  const displayName = getCollectionDisplayName(collection.name);

                return (
                  <div key={index} className="p-6">
                    <div className="grid grid-cols-[1fr,auto] gap-6 items-center">
                      {/* Left side: Icon, name, and badges */}
                      <div className="flex items-start space-x-4">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-mint-muted)" }}>
                          <IconComponent className="h-4 w-4" style={{ color: "var(--color-mint)" }} />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-medium" style={{ color: "var(--color-text-primary)" }}>
                            {displayName}
                          </h4>
                          <p className="text-xs font-mono" style={{ color: "var(--color-text-muted)" }}>
                            {collection.name}
                          </p>

                          {/* Chat Breakdown - Only show if more than 1 active chat */}
                          {activeChatsCount > 1 && collection.chat_breakdown && collection.chat_breakdown.length > 0 && (
                            <div className="mt-2">
                              <div className="flex items-center space-x-2 flex-wrap gap-2">
                                {collection.chat_breakdown.map((chatStat, idx) => {
                                  // Use red badge for "No chat assigned"
                                  const isNoChat = !chatStat.chat_id || !chatStat.chat_name;
                                  const badgeClass = isNoChat
                                    ? "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border border-red-200 text-red-600 bg-red-50"
                                    : "badge-mint inline-flex items-center";

                                  // Use translation for "No chat assigned"
                                  const displayName = isNoChat
                                    ? (translate("noChatsAssigned") || "Sem chat atribuído")
                                    : chatStat.chat_name;

                                  return (
                                    <span
                                      key={idx}
                                      className={badgeClass}
                                      title={`${displayName}: ${chatStat.vectors_count} ${translate("vectors") || "vetores"}`}
                                    >
                                      {displayName}: <strong className="ml-1">{chatStat.vectors_count.toLocaleString()}</strong>
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right side: Statistics (will be centered vertically by grid) */}
                      <div className="flex items-center space-x-6">
                        <div
                          className="text-center"
                          title="Número total de vetores nesta coleção"
                        >
                          <p className="text-xs font-mono" style={{ color: "var(--color-text-muted)" }}>
                            {translate("vectors") || "Vectors"}
                          </p>
                          <p className="text-xl font-semibold" style={{ color: "var(--color-text-primary)" }}>
                            {collection.vectors_count.toLocaleString()}
                          </p>
                        </div>

                        <div
                          className="text-center"
                          title="Dimensão dos vetores (3072 = embedding size)"
                        >
                          <p className="text-xs font-mono" style={{ color: "var(--color-text-muted)" }}>
                            {translate("vectorSize") || "Vector Size"}
                          </p>
                          <p className="text-xl font-semibold" style={{ color: "var(--color-text-primary)" }}>
                            {collection.vector_size}
                          </p>
                        </div>

                        <div
                          className="text-center"
                          title="Métrica usada para calcular similaridade entre vetores (Cosine = cosseno do ângulo entre vetores)"
                        >
                          <p className="text-xs font-mono" style={{ color: "var(--color-text-muted)" }}>
                            {translate("distance") || "Distance"}
                          </p>
                          <p className="text-xl font-semibold" style={{ color: "var(--color-text-primary)" }}>
                            {collection.distance}
                          </p>
                        </div>

                        {isEffectivelyAdmin() && (
                          <button
                            onClick={() =>
                              handleClearCollection(collection.name)
                            }
                            disabled={loading}
                            className="btn-destructive flex items-center space-x-1 disabled:opacity-50"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span>{translate("clear") || "Clear"}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            ref={confirmModalRef}
            className="card rounded-xl max-w-md w-full mx-4" style={{ backgroundColor: "var(--color-surface)" }}
          >
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-6 w-6" style={{ color: "var(--color-error)" }} />
                </div>
                <div className="ml-3">
                  <h3 className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>
                    {confirmAction.title.replace(
                      "{collectionName}",
                      confirmAction.collectionName || ""
                    )}
                  </h3>
                </div>
              </div>

              <div className="mb-6">
                <div className="rounded-xl p-4 mb-4 border border-[var(--color-border)]" style={{ backgroundColor: "var(--color-surface-raised)" }}>
                  <div className="flex items-center mb-2">
                    <Database className="h-5 w-5 mr-2" style={{ color: "var(--color-mint)" }} />
                    <span className="font-medium" style={{ color: "var(--color-text-primary)" }}>
                      {confirmAction.description}
                    </span>
                  </div>
                  <div className="text-sm whitespace-pre-line" style={{ color: "var(--color-text-secondary)" }}>
                    {confirmAction.details}
                  </div>
                </div>

                <div className="flex items-center p-3 rounded-xl" style={{ backgroundColor: "var(--color-error-muted, #fef2f2)", border: "1px solid var(--color-error-border, #fecaca)" }}>
                  <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" style={{ color: "var(--color-error)" }} />
                  <span className="text-sm font-medium" style={{ color: "var(--color-error)" }}>
                    {confirmAction.warning}
                  </span>
                </div>
              </div>

              {/* Confirmation Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-text-secondary)" }}>
                  {translate("typeToConfirm") || "Type to confirm"}:
                </label>
                <div className="text-sm mb-2" style={{ color: "var(--color-text-secondary)" }}>
                  {translate("typeConfirmationText") || "Type"}{" "}
                  <span className="font-mono font-bold text-red-500">
                    {confirmAction.confirmationText}
                  </span>{" "}
                  {translate("toProceed") || "to proceed"}
                </div>
                <input
                  type="text"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder={confirmAction.confirmationText}
                  className="field-input w-full"
                  disabled={loading}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCloseConfirmModal}
                  disabled={loading}
                  className="btn-secondary disabled:opacity-50"
                >
                  {translate("cancel") || "Cancel"}
                </button>
                <button
                  onClick={() => {
                    if (confirmAction.type === "collection") {
                      executeClearCollection();
                    } else if (confirmAction.type === "all") {
                      executeClearAll();
                    }
                  }}
                  disabled={
                    loading ||
                    confirmationText !== confirmAction.confirmationText
                  }
                  className="btn-destructive flex items-center disabled:opacity-50"
                >
                  {loading && (
                    <div style={{ width: '16px', height: '16px', border: '2px solid #ef4444', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: '8px' }} />
                  )}
                  {translate("confirm") || "Confirm"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VectorStoreStats;
